/**
 * /about — content, verbatim from the supplied about.md.
 *
 * FOUR CORRECTIONS TO THE BRIEF, all recorded so they are not re-litigated:
 *
 * 1. STACK. The brief says "the WeAreQA Astro site". This is Next.js 16
 *    (`package.json`). Astro belonged to the superseded v1 direction.
 * 2. `02-DESIGN-BRIEF.md` lives in `design/archive-v1/` and is SUPERSEDED.
 *    Taking "these exact values" from it would import Sora, JetBrains Mono,
 *    blue #0052F7 and orange #FC9606 — none of which exist in the approved
 *    palette, and all of which CLAUDE.md forbids. This page uses the approved
 *    design system, same as the other twenty.
 * 3. `01-IA-SITEMAP.md` is also archive-v1 and describes a different site (35
 *    URLs, different nav). The live header and footer are used instead.
 * 4. `04-SEO-SPEC.md` IS active and was followed — the Organization and Person
 *    schema below are lifted from it rather than composed.
 *
 * COPY IS VERBATIM. One character changed: the source's draft warning opens
 * with a warning-sign emoji, and "no emoji" is a non-negotiable in CLAUDE.md.
 * The words are untouched.
 *
 * The leadership section is scaffolding the source itself marks as draft, and
 * it renders with that warning intact and visible because the source says
 * "nothing here should ship unchecked".
 *
 * IT IS ALSO NOW MIS-PREMISED. The source wrote it as a founder origin story;
 * the client is CEO and did not found the company. Every founder assertion has
 * been removed — the page title, the photo alt, the opener, the Person
 * jobTitle, and Organization.founder, which is dropped entirely rather than
 * reassigned to someone unknown.
 */

export const seo = {
  title: "About WeAreQA — Quality Engineering",
  description:
    "Senior quality engineering for VP Engineering and CTOs in financial services, healthcare, pharma, SaaS and communications. Seven engagements, 4–14 months.",
} as const;

export const hero = {
  heading: "About WeAreQA",
  body: [
    "WeAreQA is a quality engineering consultancy. We work with VP Engineering and CTOs at mid-market companies across the US, Canada, UK and Ireland — in financial services, healthcare, pharma, e-commerce and SaaS, and communications.",
  ],
  /*
   * The second sentence used to deny that WeAreQA supplies testers --
   * untrue once WeAreQA both provides engineers and helps companies hire them
   * directly (owner, 2026-09-30). The line stays; `people` says the rest in
   * body type, because at this size a second sentence would dwarf the first.
   */
  turn: "We rebuild how quality gets delivered.",
  people: {
    lead: "When you need people, we have them: QA engineers we provide and manage, or engineers you ",
    link: { label: "hire directly through us", href: "/for-employers" },
    tail: ", with no recruitment fee.",
  },
} as const;

/**
 * PLACEHOLDERS REMOVED at the client's request, and the scaffolding with them.
 *
 * What was here was never page copy. The source marked the whole block DRAFT —
 * "scaffolding written to be edited, not facts" — so removing the gap markers
 * while keeping the prose would have shipped half-sentences and editorial
 * advice as if they were finished. Gone:
 *
 *   - "I've spent eleven years in quality engineering. [which companies…]"
 *   - "Most of that time I watched the same thing happen… [what was the
 *     problem, in your words?]"
 *   - The Basecamp guidance panel, which was advice to the client, not copy.
 *   - The note that the section needed re-scoping for a CEO rather than a
 *     founder.
 *
 * STILL TRUE AND STILL OUTSTANDING: this section has no story in it. It is a
 * photograph and a job title. Whatever a VP Engineering should believe about
 * the person running the company is not on the page. All of the above is
 * recoverable from git.
 */
/**
 * RESTORED at the client's request, minus the scaffolding.
 *
 * The two paragraphs below are the source's own words and stand as complete
 * prose once the gap markers are gone. Deliberately not restored:
 *
 *   - The DRAFT warning panel — the client asked for no placeholders.
 *   - The Basecamp guidance panel — that was advice written to the client
 *     about how to write this section, never page copy.
 *   - The two founder-premise sentences, which were false: he is CEO, not the
 *     founder. See the correction recorded in CLAUDE.md.
 *
 * STILL THIN, and worth saying: the second paragraph ends on "the people
 * weren't the problem" without ever saying what the problem was. That was the
 * gap marker's job. As it stands the section poses a question and leaves it,
 * and the eleven years carry no detail — no companies, no roles, no domains.
 * One or two sentences from the client would finish it.
 */
export const founder = {
  display: "The person behind it",
  photo: {
    src: "/assets/ceo-stepan.jpg",
    alt: "Stepan Shykerynets, CEO of WeAreQA",
    width: 1163,
    height: 1320,
  },
  opener: "I'm Stepan Shykerynets, CEO of WeAreQA.",
  paragraphs: [
    "I've spent more than 15 years in quality engineering.",
    "Most of that time I watched the same thing happen. A company would decide quality mattered, buy a tool or hire testers, and eighteen months later the release cycle was no shorter and the defects were no fewer. The tool wasn't the problem. The people weren't the problem.",
  ],
} as const;
/**
 * Duration and QA team size were removed from the table at the client's
 * request, leaving domain and region.
 *
 * Both figures survive in the lead sentence — "four to fourteen months each,
 * QA teams of three to eleven people" — which is deliberate. The source argues
 * that the shape is what makes "we" legitimate without named clients, and the
 * section's own closing line promises "how long, how many people". Dropping the
 * columns shortens the table; dropping the sentence too would have removed the
 * proof the section exists to give.
 */
export const delivered = {
  display: "What we've delivered",
  lead: "Seven engagements. Four to fourteen months each. QA teams of three to eleven people.",
  columns: ["Domain", "Region"],
  rows: [
    ["Pharma — customer engagement platform", "US"],
    ["Pharma — life sciences AI analytics", "US"],
    ["Pharma — sample management, IQ/OQ validation", "US"],
    ["Healthcare — patient care app, HIPAA scope", "US"],
    ["Financial services — global investment reporting", "Worldwide"],
    ["Financial services — foreclosure auction platform", "US"],
    ["Government — national mortgage platform", "Middle East"],
  ],
  nda: "We can't name these clients. Every one is under NDA, and we'd rather give up some persuasive weight than be the vendor who named the last one.",
  shape:
    "The last row served millions of citizens on a government's first large-scale digital transformation.",
  link: { label: "Read the case studies", href: "/case-studies" },
} as const;
/**
 * The client chose a static review card over Clutch's widget, so this section
 * ships as real HTML with no third-party script — which keeps the page's
 * zero-client-JS requirement intact.
 *
 * WHAT IS HERE AND WHAT IS NOT. The rating and review count are the approved
 * site metric from CLAUDE.md. The profile and the specific review link are the
 * client's own URLs. The quote itself is NOT here: Clutch returns 403 to
 * server-side requests, so it could not be read, and a client testimonial is
 * the last thing that should ever be reconstructed from memory. The slot stays
 * visibly empty until the client pastes the text.
 *
 * The client described this as an Upwork review while supplying a clutch.co
 * URL. Worth confirming which platform it belongs to before publishing.
 */
export const clients = {
  display: "What clients say",
  body: "Our reviews on Clutch are verified directly with the reviewers. With no client names and no logos on this site, that's the proof we point at rather than a testimonial we could have written ourselves.",
  /** Approved metric — CLAUDE.md. Never round, never add a third figure. */
  rating: "5.0",
  ratingLabel: "on Clutch",
  reviewCount: "4 verified reviews",
  breakdown: "Quality, schedule, cost and NPS all 5.0",
  profile: { label: "See all reviews on Clutch", href: "https://clutch.co/profile/weareqa" },
  /**
   * Clutch's own widget, per the client. It needs the numeric Clutch company
   * ID, which is not derivable from the profile slug or the review URL and
   * could not be looked up — Clutch 403s server-side requests. Until it is
   * supplied the widget renders nothing and the static card stands alone.
   */
  /**
   * The client's own embed snippet, transcribed exactly. Type 2 at 45px is the
   * compact rating badge rather than a review list, which is why it loads on
   * scroll rather than behind a button — see components/ui/ClutchWidget.tsx.
   *
   * `nofollow: "false"` is theirs. It means the badge's outbound links are
   * followed; worth a second look if link equity to clutch.co is not wanted.
   */
  /**
   * The client's second snippet, transcribed exactly. Type 12 at 375px is the
   * full review list, pinned to four specific reviews, and it replaces the
   * 45px rating badge and the static card that stood beside it.
   */
  widget: {
    companyId: "1917970",
    widgetType: "12",
    height: "375",
    nofollow: "false",
    expandifr: "true",
    reviews: "432498,281846,160515,158818",
  },  review: {
    href: "https://clutch.co/go-to-review/c932bf13-a5aa-4eee-8c88-7da95a934462/432498",
    label: "Read this review on Clutch",
  },
} as const;

export const howWeWork = {
  display: "How we work",
  items: [
    {
      title: "Senior people do the work.",
      body: "The person who scopes your engagement is on it. We're a core team of senior practitioners, extended with specialists we've worked with before when an engagement needs particular depth. We don't keep a bench of juniors to place.",
    },
    {
      title: "We take a small number of clients at a time.",
      body: "That's a real constraint and we'd rather say it than have you find out. If you need twenty testers next month, we'll tell you on the first call that we're the wrong shape for it.",
    },
    {
      title: "We say what we can't do.",
      body: "Every service page on this site has a section listing what that service isn't for. It isn't modesty — it's the quickest way for both of us to find out whether this is a fit before anyone spends money.",
    },
    {
      title: "AI is in how we work, not in what we sell you.",
      body: "We use AI across test design, requirement analysis, defect intelligence and reporting because it makes the work better, and we test AI products for clients who ship them. What we don't do is sell “AI” as the outcome.",
    },
  ],
  /** Route does not exist yet; held at "#" rather than shipping a dead link. */
  link: { label: "How we approach quality engineering", href: "#" },
} as const;

export const whoWeWorkWith = {
  display: "Who we work with",
  body: "VP Engineering and CTOs at mid-market companies — teams big enough that quality has become a structural problem, small enough that building a full quality function in-house isn't the answer.",
  sectorsLabel: "Sectors",
  sectors: [
    "Financial services",
    "Healthcare and healthtech",
    "Pharma",
    "E-commerce and SaaS",
    "Communications",
  ],
  regionsLabel: "Regions",
  regions: ["US and Canada", "UK and Ireland"],
} as const;

/**
 * REMOVED at the client's request: the "Practical details" section.
 *
 * It carried three blocks, and two were substantive — worth knowing before any
 * of it is reinstated elsewhere:
 *
 *   1. Two entities. US and Ukrainian companies, with US clients contracting
 *      the US entity to avoid foreign-vendor onboarding and cross-border
 *      payment friction. A real commercial differentiator, now stated nowhere.
 *   2. Data and security. GDPR compliant, NDA from first contact, and the
 *      explicit admission of holding neither SOC 2 nor ISO 27001. That
 *      disclosure matched the site's voice and is now absent.
 *   3. Founded — a gap the footer already answers with 2019.
 *
 * Recoverable from git if any of it should move to /security or the footer.
 */
export const talk = {
  display: "Talk to us",
  body: "If any of this sounds like your situation, a conversation is the fastest way to find out whether we can help.",
} as const;

/**
 * Schema, taken from `design/04-SEO-SPEC.md` (active) and the page source.
 *
 * VISIBLE-TEXT RULE. The brief requires every schema property to have matching
 * visible text. Two `knowsAbout` values from the page source have none on this
 * page — "Test automation architecture" and "Computer software assurance" — so
 * they are omitted here rather than shipped unsupported. Restoring them is a
 * one-line change if the copy grows to cover them.
 *
 * `<CLUTCH_PROFILE_URL>` is a NEEDS in both the source and the SEO spec, so it
 * is left out of `sameAs` entirely rather than emitted as a broken placeholder.
 */
export const schema = {
  /*
   * No `organization` here any more. This file used to define `#organization`
   * a second time, as a ProfessionalService whose description ("AI-native
   * quality engineering") appears nowhere on the page and whose url differed
   * from the home page's. The company is defined once, in
   * `src/config/structuredData.ts`, and /about emits that node through the page
   * registry. The four countries it listed moved there with it.
   */
  person: {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://weareqa.com/#stepan",
    name: "Stepan Shykerynets",
    jobTitle: "Chief Executive Officer",
    worksFor: { "@id": "https://weareqa.com/#organization" },
    knowsAbout: ["Quality engineering", "AI in software testing"],
    sameAs: ["https://www.linkedin.com/in/stepan-shykerynets"],
  },
  aboutPage: {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://weareqa.com/about#page",
    url: "https://weareqa.com/about",
    name: "About WeAreQA — Quality Engineering",
    mainEntity: { "@id": "https://weareqa.com/#organization" },
  },
} as const;

/** Rendered as real HTML comments so they cannot ship unnoticed. */
/**
 * Kept as real HTML comments in the served markup. The visible placeholders are
 * gone at the client's request; these are the record that the gaps are not.
 */
export const needsComments = [
  "OUTSTANDING: the leadership section names no companies, roles or domains, and never says what the problem was",
  "OUTSTANDING: direct email address for the Talk to us section",
] as const;
